import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'drink_guidance_state';

type GuidanceStep = 'initial' | 'trial_complete' | 'first_drink_logged' | 'complete';

interface GuidanceState {
  step: GuidanceStep;
  hasCompletedGuidance: boolean;
}

const DEFAULT_STATE: GuidanceState = {
  step: 'initial',
  hasCompletedGuidance: false,
};

export function useDrinkGuidance(existingDrinkCount: number) {
  const [state, setState] = useState<GuidanceState>(() => {
    // Check localStorage on init
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch {
      // Ignore parsing errors
    }
    return DEFAULT_STATE;
  });

  // Persist state changes to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Ignore storage errors
    }
  }, [state]);

  // If user already has drinks logged, mark guidance as complete
  useEffect(() => {
    if (existingDrinkCount > 0 && !state.hasCompletedGuidance) {
      setState({
        step: 'complete',
        hasCompletedGuidance: true,
      });
    }
  }, [existingDrinkCount, state.hasCompletedGuidance]);

  const showTooltip = !state.hasCompletedGuidance && state.step !== 'complete';
  const isTrialTap = state.step === 'initial';
  const isReadyForFirstDrink = state.step === 'trial_complete';

  const handleTrialComplete = useCallback(() => {
    setState(prev => ({
      ...prev,
      step: 'trial_complete',
    }));
  }, []);

  const handleFirstDrinkLogged = useCallback(() => {
    setState({
      step: 'complete',
      hasCompletedGuidance: true,
    });
  }, []);

  return {
    showTooltip,
    isTrialTap,
    isReadyForFirstDrink,
    currentStep: state.step,
    handleTrialComplete,
    handleFirstDrinkLogged,
  };
}
